import { useEffect, useState } from 'react';
import {
  getAdminTrainingRequests, processTrainingRequest,
  GetAdminTrainingRequestsOutputType,
} from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Award, Loader2, ExternalLink } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type TrainingRequest = GetAdminTrainingRequestsOutputType['requests'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };
const fmtCost = (n?: number) => n != null ? `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0 })}` : '—';

export default function TrainingRequestsAdminTab() {
  const [requests, setRequests] = useState<TrainingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Pending');
  const [actionDialog, setActionDialog] = useState<{ request: TrainingRequest; action: 'approve' | 'decline' | 'complete' } | null>(null);
  const [confirmedCost, setConfirmedCost] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [completedDate, setCompletedDate] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = () => {
    setLoading(true);
    getAdminTrainingRequests({}).then(d => setRequests(d.requests)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAction = (req: TrainingRequest, action: 'approve' | 'decline' | 'complete') => {
    setActionDialog({ request: req, action });
    setConfirmedCost(req.estimatedCost?.toString() || '');
    setReviewNotes(req.reviewNotes || '');
    setCompletedDate('');
  };

  const handleAction = async () => {
    if (!actionDialog) return;
    if (actionDialog.action === 'decline' && !reviewNotes.trim()) { toast.error('Please provide a reason'); return; }
    setProcessing(true);
    try {
      await processTrainingRequest({
        requestId: actionDialog.request.id,
        action: actionDialog.action,
        confirmedCost: confirmedCost ? parseFloat(confirmedCost) : undefined,
        reviewNotes: reviewNotes || undefined,
        completedDate: completedDate || undefined,
      });
      toast.success(`Request ${actionDialog.action}d`);
      setActionDialog(null);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setProcessing(false); }
  };

  const counts = { Pending: 0, Approved: 0, Declined: 0, Completed: 0 };
  for (const r of requests) { if (r.status in counts) counts[r.status as keyof typeof counts]++; }

  const filtered = requests.filter(r => r.status === filter);

  if (loading) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {(['Pending', 'Approved', 'Declined', 'Completed'] as const).map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5 ${filter === s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
          >
            {s}
            {counts[s] > 0 && <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${filter === s ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-background text-foreground'}`}>{counts[s]}</span>}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Award className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No {filter.toLowerCase()} training requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => (
            <div key={req.id} className="border border-border rounded-lg bg-card p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="text-sm font-medium text-foreground">{req.courseName}</p>
                  <p className="text-xs text-muted-foreground">{req.employeeName}{req.courseType ? ` · ${req.courseType}` : ''}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-sm whitespace-nowrap ${req.status === 'Approved' || req.status === 'Completed' ? 'bg-primary/10 text-primary' : req.status === 'Declined' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                  {req.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground mb-2">
                {req.provider && <span>Provider: {req.provider}</span>}
                {req.estimatedCost != null && <span>Est: {fmtCost(req.estimatedCost)}</span>}
                {req.confirmedCost != null && <span className="text-primary">Confirmed: {fmtCost(req.confirmedCost)}</span>}
                {req.submittedAt && <span>Submitted {fmtDate(req.submittedAt)}</span>}
              </div>
              {req.justification && <p className="text-xs text-muted-foreground italic mb-3">"{req.justification}"</p>}
              {req.reviewNotes && <p className="text-xs text-muted-foreground bg-muted/40 px-3 py-2 rounded mb-3">{req.reviewNotes}</p>}
              <div className="flex gap-2 flex-wrap">
                {req.status === 'Pending' && (
                  <>
                    <Button size="sm" className="bg-primary text-primary-foreground h-7 text-xs" onClick={() => openAction(req, 'approve')}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-destructive/40 text-destructive" onClick={() => openAction(req, 'decline')}>
                      <XCircle className="w-3 h-3 mr-1" /> Decline
                    </Button>
                  </>
                )}
                {req.status === 'Approved' && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openAction(req, 'complete')}>
                    <Award className="w-3 h-3 mr-1" /> Mark Completed
                  </Button>
                )}
                {req.certificate.length > 0 && (
                  <a href={req.certificate[0].url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <ExternalLink className="w-3 h-3" /> Certificate
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.action === 'approve' ? 'Approve' : actionDialog?.action === 'decline' ? 'Decline' : 'Mark Completed'}: {actionDialog?.request.courseName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {actionDialog?.action === 'approve' && (
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Confirmed Cost (£)</label>
                <Input type="number" value={confirmedCost} onChange={e => setConfirmedCost(e.target.value)} placeholder="0" />
              </div>
            )}
            {actionDialog?.action === 'complete' && (
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Completed Date</label>
                <Input type="date" value={completedDate} onChange={e => setCompletedDate(e.target.value)} />
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
                {actionDialog?.action === 'decline' ? 'Reason *' : 'Notes (optional)'}
              </label>
              <Textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} className="min-h-16" placeholder={actionDialog?.action === 'decline' ? 'Explain why...' : 'Optional notes...'} />
            </div>
            <Button onClick={handleAction} disabled={processing} className="w-full bg-primary text-primary-foreground">
              {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : 'Confirm'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
